import { ApiBaseService, ApiService, ApiV2Service } from './api.base.service';
import store from '@/store/store';
import {
  AdminSubscriptionsResponse, Audit,
  SigfoxSubscriptionPool,
  SimCard, Subscription,
  SubscriptionCounts,
  SubscriptionOrder,
  SubscriptionPlan,
  SubscriptionPlanRequest,
  SubscriptionPlanType,
  SubscriptionProduct,
  SubscriptionsResponse,
} from '@/types';
import Transformation from '@/classes/transformation';
import moment from 'moment';

export class SubscriptionsService {
  private api: ApiBaseService;
  private apiV2: ApiBaseService;

  constructor() {
    this.api = ApiService;
    this.apiV2 = ApiV2Service;
  }

  public async getSigfoxSubscriptionPools(): Promise<SigfoxSubscriptionPool[]> {
    return this.api.get(`subscriptionPools/sigfox`);
  }

  public async createSigfoxSubscriptionPool(sigfoxPool: any): Promise<SigfoxSubscriptionPool> {
    return this.api.post(`subscriptionPools/sigfox`, sigfoxPool);
  }

  public async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    return this.api.get(`subscriptionPlans`);
  }

  public async getSubscriptionPlansForOrganisation(): Promise<SubscriptionPlan[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/subscriptionPlans`);
  }

  public async getSubscriptionPlansForSpecifiedOrganisation(organisation: string, hasSims: boolean): Promise<SubscriptionPlan[]> {
    return this.api.get(`organisation/${organisation}/subscriptionPlans${hasSims ? '?hasSims=true' : ''}`);
  }

  public async createSubscriptionPlan(subscriptionPlanRequest: SubscriptionPlanRequest): Promise<SubscriptionPlan> {
    return this.api.post('subscriptionPlans', subscriptionPlanRequest);
  }

  public async updateSubscriptionPlan(subscriptionPlanRequest: SubscriptionPlanRequest): Promise<SubscriptionPlan> {
    // Redact to the updatable properties
    const body: any = {
      name: subscriptionPlanRequest.name,
      start_at: subscriptionPlanRequest.start_at,
      activate_by: subscriptionPlanRequest.activate_by,
      term_length: subscriptionPlanRequest.term_length,
      total_subscriptions: subscriptionPlanRequest.total_subscriptions,
      type: subscriptionPlanRequest.type,
    };
    return this.api.put(`subscriptionPlans/${subscriptionPlanRequest.id}`, body);
  }

  public async getSubscriptionTypes(hasSims: boolean): Promise<SubscriptionPlanType[]> {
    return this.api.get(`subscriptionPlans/types${hasSims ? '?hasSims=true' : ''}`);
  }

  public async getSimsForSubscriptionPlan(subscriptionPlanId: number): Promise<SimCard[]> {
    return this.apiV2.get(`sims/subscriptionPlans/${subscriptionPlanId}`);
  }

  public async getSimsForSubscriptionOrder(subscriptionOrderId: number): Promise<SimCard[]> {
    return this.apiV2.get(`sims/subscriptionOrders/${subscriptionOrderId}`);
  }

  public async getSubscriptionProducts(): Promise<SubscriptionProduct[]> {
    return this.apiV2.get(`admin/subscriptions/products`);
  }

  public async addSubscriptionProduct(subscriptionProduct: SubscriptionProduct): Promise<SubscriptionProduct> {
    return this.apiV2.post(`admin/subscriptions/products`, subscriptionProduct);
  }

  public async updateSubscriptionProduct(subscriptionProduct: SubscriptionProduct): Promise<SubscriptionProduct> {
    return this.apiV2.put(`admin/subscriptions/products/${subscriptionProduct.id}`, subscriptionProduct);
  }

  public async getSubscriptionOrders(): Promise<SubscriptionOrder[]> {
    return this.apiV2.get(`admin/subscriptions/orders`);
  }

  public async createSubscriptionOrder(subscriptionOrder: SubscriptionOrder): Promise<SubscriptionOrder> {
    return this.apiV2.post(`admin/subscriptions/orders`, subscriptionOrder);
  }

  public async getOrganisationSubscriptionOrders(
    showExpired: boolean = true,
    take: number = 0, skip: number = 0, sort: string | string[] = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {},
    search: string | null = null): Promise<{ subscriptionOrders: SubscriptionOrder[]; columnFilters: any; total: number }> {
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);
    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (search) {
      filtersString += `&searchNames=${encodeURIComponent(search)}`;
    }
    return this.apiV2.get(`admin/subscriptions/orders/new?showExpired=${showExpired}&take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async createOrganisationsSubscriptionOrder(body: any): Promise<SubscriptionOrder> {
    return this.apiV2.post(`admin/subscriptions/orders/add`, body);
  }

  public async updateOrganisationsSubscriptionOrder(subscriptionOrderId: number, body: any): Promise<SubscriptionOrder> {
    return this.apiV2.put(`admin/subscriptions/orders/${subscriptionOrderId}/update`, body);
  }

  public async updateSubscriptionOrder(subscriptionOrder: SubscriptionOrder): Promise<SubscriptionOrder> {
    return this.apiV2.put(`admin/subscriptions/orders/${subscriptionOrder.id}`, subscriptionOrder);
  }

  public async getSubscriptionCounts(orgId: number): Promise<SubscriptionCounts> {
    return this.apiV2.get(`organisation/${orgId}/subscriptions/counts`);
  }

  public async getAdminSubscriptions(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false,
    filters: Record<string, Record<string, boolean>> = {},
    timeFilters: { [key: string]: { start: Date | null; end: Date | null }} = {},
    params: {
     organisationId?: number;
     accountId?: number;
     subscriptionOrderId?: number;
     projectId?: number;
     search?: string;
     expiresBetween?: { start: Date | string; end: Date | string };
     expired?: boolean;
     excludeExpired?: boolean;
     excludeLapsed?: boolean;
     excludeActive?: boolean;
     excludeNotRenewing?: boolean;
     excludeDetached?: boolean;
     excludeAvailable?: boolean;
    }): Promise<{ items: Subscription[], counts: { [key: string]: number }, total: number, columnFilters: any }> {
    let filtersString = '';
    const filterQuery = Transformation.filterMapToQueryString(filters);
    const timeFilterQuery = Transformation.timeFilterMapToQueryString(timeFilters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (timeFilterQuery) {
      filtersString += `&${timeFilterQuery}`;
    }
    if (params.organisationId) {
      filtersString += `&organisationId=${params.organisationId.toString()}`;
    }
    if (params.accountId) {
      filtersString += `&accountId=${params.accountId.toString()}`;
    }
    if (params.projectId) {
      filtersString += `&projectId=${params.projectId.toString()}`;
    }
    if (params.expired) {
      filtersString += `&expired=true`;
    }
    if (params.expiresBetween) {
      filtersString += `&start=${moment(params.expiresBetween.start).format('YYYY-MM-DD')}`;
      filtersString += `&end=${moment(params.expiresBetween.end).format('YYYY-MM-DD')}`;
    }
    if (params.excludeExpired) {
      filtersString += `&excludeExpired=true`;
    }
    if (params.excludeLapsed) {
      filtersString += `&excludeLapsed=true`;
    }
    if (params.excludeActive) {
      filtersString += `&excludeActive=true`;
    }
    if (params.excludeAvailable) {
      filtersString += `&excludeAvailable=true`;
    }
    if (params.excludeNotRenewing) {
      filtersString += `&excludeNotRenewing=true`;
    }
    if (params.excludeDetached) {
      filtersString += `&excludeDetached=true`;
    }
    if (params.search) {
      filtersString += `&searchString=${encodeURIComponent(params.search)}`;
    }
    return this.apiV2.get(`admin/subscriptions?take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

public async getAccountSubscriptions(params: {
    take: number;
    skip?: number;
    sort?: string;
    desc?: boolean;
    accountId?: number;
    organisationId?: number;
    projectId?: number;
    searchString?: string;
    expiresBetween?: { start: Date | string; end: Date | string };
    expired?: boolean;
    excludeExpired?: boolean;
    excludeLapsed?: boolean;
    excludeActive?: boolean;
    excludeNotRenewing?: boolean;
    excludeDetached?: boolean;
    excludeAvailable?: boolean;
  }): Promise<SubscriptionsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('take', params.take.toString());
    if (params.skip) {
      queryParams.append('skip', params.skip.toString());
    }
    if (params.sort) {
      queryParams.append('sort', params.sort);
    }
    if (params.desc) {
      queryParams.append('desc', 'true');
    }
    if (params.organisationId) {
      queryParams.append('organisationId', params.organisationId.toString());
    }
    if (params.projectId) {
      queryParams.append('projectId', params.projectId.toString());
    }
    if (params.searchString) {
      queryParams.append('searchString', params.searchString);
    }
    if (params.expired) {
      queryParams.append('expired', 'true');
    }
    if (params.expiresBetween) {
      queryParams.append('start', moment(params.expiresBetween.start).format('YYYY-MM-DD'));
      queryParams.append('end', moment(params.expiresBetween.end).format('YYYY-MM-DD'));
    }
    if (params.excludeExpired) {
      queryParams.append('excludeExpired', 'true');
    }
    if (params.excludeLapsed) {
      queryParams.append('excludeLapsed', 'true');
    }
    if (params.excludeDetached) {
      queryParams.append('excludeDetached', 'true');
    }
    if (params.excludeNotRenewing) {
      queryParams.append('excludeNotRenewing', 'true');
    }
    if (params.excludeAvailable) {
      queryParams.append('excludeAvailable', 'true');
    }
    if (params.excludeActive) {
      queryParams.append('excludeActive', 'true');
    }
    const queryString = `?${queryParams.toString()}`;
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/subscriptions${queryString}`);
  }

  public async getSubscriptionProductFromOrderId(orderId: number): Promise<{ name: string; sim_based: boolean }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/subscriptions/products/${orderId}`);
  }

  public async canUnsubscribeDeviceCheck(deviceId: number): Promise<{ result: boolean; reason?: string; }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/devices/${deviceId}/canUnsubscribe`);
  }

  public async canUnsubscribeGatewayCheck(gatewayId: number): Promise<{ result: boolean; reason?: string; }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/gateways/${gatewayId}/canUnsubscribe`);
  }

  public async unsubscribe(deviceId: number): Promise<{ success: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.put(`organisation/${organisation}/devices/${deviceId}/deactivate`, {});
  }

  public async unsubscribeGateway(gatewayId: number): Promise<{ success: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.put(`organisation/${organisation}/gateways/${gatewayId}/deactivate`, {});
  }

  public async toggleAutoRenew(subscriptionId: string): Promise<void> {
    const organisation = store.getters.activeOrganisation[0].id;
    await this.apiV2.put(`organisation/${organisation}/subscriptions/${subscriptionId}/autorenew`, {});
  }

  public async setAutoRenew(subscriptionId: string, autoRenew: boolean): Promise<void> {
    const organisation = store.getters.activeOrganisation[0].id;
    await this.apiV2.put(`organisation/${organisation}/subscriptions/${subscriptionId}/autorenew?autoRenew=${autoRenew ? 'true' : 'false'}`, {});
  }

  public async adminExport(params: any): Promise<{ job_id: string }> {
    return this.apiV2.post(`admin/subscriptions/export`, params);
  }

  public async export(params: any): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/subscriptions/export`, params);
  }

  public async isDTNetworkOperator(orderId: number): Promise<{ result: boolean }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/subscriptions/products/${orderId}/isDT`);
  }

  public async getSubscriptionAudit(subscriptionId: string): Promise<Audit[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`audit/organisation/${organisation}/subscription/${subscriptionId}`);
  }

  public async getSubscriptionVolumeCounts(subscriptionId: string): Promise<{ subscriptionMessageCount: number; deviceMessageCount: number; }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/subscriptions/${subscriptionId}/volumeCounts`);
  }

  public async getVolumeCountsForMonth(subscriptionId: string, year?: number, month?: number): Promise<{ subscriptionMessageCount: number; deviceMessageCount: number; }> {
    const organisation = store.getters.activeOrganisation[0].id;
    let uri = `organisation/${organisation}/subscriptions/${subscriptionId}/volumeCounts`;
    if (year && month) {
      uri += `?year=${year}&month=${month}`;
    }

    return this.apiV2.get(uri);
  }
}

export default new SubscriptionsService();
